import { useForm, SubmitHandler } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { PreconditionFailureReason } from "../BasketService";
import { CompanyNameId } from "../Company";
import { Input } from "../components/Input";
import { ContactInformation } from "../ContactInformation";
import { Spinner } from "../components/Spinner";
import { NaviButtons } from "../components/NaviButtons";
import { EmailInput } from "../components/EmailInput";
import { phoneRegex } from "../validation";
import { translationKey } from "../i18n";
import { withExceptionCapturing } from "../common";

const reasonTranslations = new Map<PreconditionFailureReason, translationKey>([
	[PreconditionFailureReason.EXISTING_CUSTOMER, "checkExistingCustomer"],
	[PreconditionFailureReason.ACTIVITY_INTERRUPTED, "checkActivityInterrupted"],
	[PreconditionFailureReason.BANKRUPT, "checkBankrupt"],
	[PreconditionFailureReason.LIQUIDATION, "checkLiquidation"],
	[PreconditionFailureReason.COMPANY_INDUSTRY, "checkCompanyIndustry"],
	[PreconditionFailureReason.TURNOVER_TOO_HIGH, "checkTurnoverTooHigh"],
	[PreconditionFailureReason.REGISTRATION_INCOMPLETE, "checkCompanyRegistrationIncomplete"],
	[PreconditionFailureReason.CREDIT_RISK, "checkCreditRisk"],
	[PreconditionFailureReason.NO_SIGNATURE_RIGHT, "checkNoSignatureRight"]
]);

interface Inputs extends ContactInformation {
	emailConfirmation: string;
}

export const StepPreconditionCheckFailed = ({
	returnUrl,
	company,
	reason,
	firstName,
	lastName,
	sendContactInformation,
	onBack
}: {
	returnUrl: string;
	company: CompanyNameId;
	reason: PreconditionFailureReason | undefined;
	firstName?: string;
	lastName?: string;
	sendContactInformation: (contactInformation: ContactInformation) => Promise<void>;
	onBack: () => void;
}) => {
	const { t } = useTranslation();
	const {
		register,
		handleSubmit,
		formState: { errors, isSubmitting }
	} = useForm<Inputs>({
		defaultValues: {
			companyName: company.name,
			companyId: company.id,
			firstName: firstName,
			lastName: lastName
		},
		mode: "onTouched"
	});
	const onSubmit: SubmitHandler<ContactInformation> = (data) => sendContactInformation(data);
	const reasonTranslationKey = reason && reasonTranslations.has(reason) ? reasonTranslations.get(reason) : undefined;

	return (
		<>
			<div className="row">
				<div className="col-md-6 col-sm-12 col-xs-12 col-md-push-6">
					<h2 dangerouslySetInnerHTML={{ __html: t("invalidCompanyText1") }}></h2>
					<p dangerouslySetInnerHTML={{ __html: t("invalidCompanyText2") }}></p>
					<p dangerouslySetInnerHTML={{ __html: t("invalidCompanyText3") }}></p>
				</div>
				<div className="col-md-6 col-sm-12 col-xs-12 col-md-pull-6">
					<h3>{t("companyBasicInformationTitle")}</h3>
					<div className="row">
						<div className="col-md-6 col-sm-6 col-xs-12">
							<div className="form-group">
								<label>{t("companyName")}</label>
								<input {...register("companyName")} type="text" className="form-control" readOnly />
							</div>
						</div>
						<div className="col-md-6 col-sm-6 col-xs-12">
							<div className="form-group">
								<label>{t("companyId")}</label>
								<input {...register("companyId")} type="text" className="form-control" readOnly />
							</div>
						</div>
					</div>
				</div>
			</div>

			{reasonTranslationKey && (
				<div className="row">
					<div className="col-md-12 col-sm-12 col-xs-12">
						<p>{t(reasonTranslationKey)}</p>
						<p>{t("invalidCompanyPleaseEnterContact")}</p>
					</div>
				</div>
			)}

			<div className="row space-t">
				<div className="col-md-12">
					<form onSubmit={withExceptionCapturing(handleSubmit(onSubmit))}>
						<div className="row">
							<div className="col-md-9 col-sm-12 col-xs-12">
								<div className="row">
									<div className="col-md-4 col-sm-12 col-xs-12">
										<Input
											register={register}
											name="lastName"
											errors={errors}
											label="lastName"
											placeholder="lastName"
											required
											readOnly={!!lastName}
										/>
									</div>
									<div className="col-md-4 col-sm-12 col-xs-12">
										<Input
											register={register}
											name="firstName"
											errors={errors}
											label="firstName"
											placeholder="firstName"
											required
											readOnly={!!firstName}
										/>
									</div>
									<div className="col-md-4 col-sm-12 col-xs-12">
										<Input
											register={register}
											name="phone"
											errors={errors}
											inputType="tel"
											label="phone"
											placeholder="phone"
											required
											registerOptions={{
												pattern: {
													value: phoneRegex,
													message: t("invalidPhone")
												}
											}}
										/>
									</div>
								</div>
								<div className="row">
									<div className="col-md-4 col-sm-12 col-xs-12">
										<EmailInput
											register={register}
											name="email"
											errors={errors}
											label="email"
											placeholder="email"
											required
											registerOptions={{
												deps: ["emailConfirmation"]
											}}
										/>
									</div>
									<div className="col-md-4 col-sm-12 col-xs-12">
										<EmailInput
											register={register}
											name="emailConfirmation"
											errors={errors}
											label="emailRepeat"
											placeholder="emailRepeat"
											required
											registerOptions={{
												validate: {
													sameEmail: (_, values) =>
														values.email !== values.emailConfirmation ? t("emailsDontMatch") : undefined
												}
											}}
										/>
									</div>
								</div>
							</div>
						</div>

						{isSubmitting && <Spinner />}

						<NaviButtons
							backVisible
							onBack={onBack}
							cancelVisible
							onCancel={() => (window.location.href = returnUrl)}
							nextText="send"
							nextDisabled={isSubmitting}
						/>
					</form>
				</div>
			</div>
		</>
	);
};
